import { createContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocalStorage } from 'beautiful-react-hooks';

import STORAGE from 'src/constants/localStorage';
import useAuth from 'src/hooks/useAuth';

const CurrentCompanyContext = createContext({
  currentCompanyId: null,
  setCurrentCompanyId: () => {},
});

export function CurrentCompanyProvider({ children }) {
  const { user } = useAuth();
  // Same key the companies page has always used, so persistence + AuthContext logout cleanup keep working.
  const [currentCompanyId, setCurrentCompanyId] = useLocalStorage(STORAGE.COMPANIES.SELECTED, user?.companyId);

  // Provider never unmounts and useLocalStorage survives logout, so reconcile to the authed user:
  // a different/new login must not inherit the prior user's selection (reset to their own company).
  const previousUid = useRef(user?.uid);
  useEffect(() => {
    if (!user?.uid) {
      // Logged out: clear in-memory selection so even a same-user re-login starts clean.
      previousUid.current = undefined;
      if (currentCompanyId) setCurrentCompanyId(undefined);
      return;
    }
    if (user.uid !== previousUid.current) {
      previousUid.current = user.uid;
      setCurrentCompanyId(user.companyId);
      return;
    }
    if (!currentCompanyId && user?.companyId) {
      setCurrentCompanyId(user.companyId);
    }
  }, [user, currentCompanyId, setCurrentCompanyId]);

  const value = useMemo(() => ({ currentCompanyId, setCurrentCompanyId }), [currentCompanyId]);

  return <CurrentCompanyContext.Provider value={value}>{children}</CurrentCompanyContext.Provider>;
}

CurrentCompanyProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default CurrentCompanyContext;
